// @ts-strict-ignore
import * as React from 'react';
import Modal from 'antd/lib/modal/Modal';
import Form from 'antd/lib/form';
import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import Spin from 'antd/lib/spin';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Popconfirm from 'antd/lib/popconfirm';
import Switch from 'antd/lib/switch';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'antd/lib/select';
import { get, set, uniqBy } from 'lodash';
import {
    tryNewBuildDistributionBucket,
    tryUpdateBuildDistributionBucket,
    TRY_NEW_BUILD_DISTRIBUTION_BUCKET,
} from '../../actions/buildDistribution';
import { AddEditBucketMembers } from './AddEditBucketMembers';
import { globalMessage } from '../../utils/globalMessage';
import {
    BuildDistroBucketBase,
    IntegrationType,
    RunwayOrVCSUser,
    SettingField,
    SettingFieldType,
    SettingOption,
} from '../../types';
import { RootState } from '../../reducers/reducers';
import {
    selectBuildSystemIntegrations,
    selectDestinationInfoByDestinationStepConfigId,
    selectOrg,
    selectStepGraph,
    selectVCSIntegration,
} from '../../selectors/selectors';
import { displayUserNameShort } from '../../helpers/displayHelpers';
import { displayNameForIntegrationType } from '../../helpers/integrationHelpers';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { destinationsByIntegrationInstallationId } from '../Integrations';
import IntegrationIcon from '../icons/IntegrationIcon';
import { useGetBuildDistroBucketRepoContributorsQuery } from '@/middlewares/defaultApi';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { CreateBuildDistributionBucketRequest } from '../../actions/entities';
import { BuildDistributionUpdatable } from '../../actions/updatable';
import { renderFormFields } from '../../helpers/formHelpers';
import {
    formFieldsFromSettingFields,
    SettingFormFieldResolver,
    SettingOptionsSourceContext,
} from '../../helpers/settingFieldHelpers';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { useBuildDistroBucketSettingFields } from '@/utils/hooks/useBuildDistroBucketSettingFields';
import TooltipInfoIcon from '../helperComponents/TooltipInfoIcon';

// The backend's group for the rule's fields, which the modal draws as one card
const RULE_GROUP = 'rule';
const WORKFLOW_ARGUMENTS_GAP = 8;
const BUILD_SYSTEM_KEY = 'buildSystemIntegrationInstallationId';
const NAME_KEY = 'name';
const NEW_BUCKET_NAME = 'New bucket';
const VCS_USERS_KEY = 'rule.filterByVCSUsers';

// Workflow arguments are an add button under the workflow, not a section of their own; the bucket's switches have no On/Off text
const resolveBucketFormField: SettingFormFieldResolver = (settingField, formField) => {
    // Only a new bucket has no saved name; config files must still name theirs, so the backend has no default
    if (settingField.key === NAME_KEY && !settingField.currentValue) {
        return { ...formField, initialValue: NEW_BUCKET_NAME };
    }
    // Their options show the integration's destinations and the users' VCS, which the backend's text labels can't
    if (settingField.key === BUILD_SYSTEM_KEY) {
        return { ...formField, renderField: () => <BuildSystemSelect settingField={settingField} /> };
    }
    if (settingField.key === VCS_USERS_KEY) {
        return { ...formField, renderField: () => <VCSUsersSelect /> };
    }
    switch (settingField.type) {
        case SettingFieldType.KeyValue:
            return { ...formField, style: { marginTop: WORKFLOW_ARGUMENTS_GAP } };
        case SettingFieldType.Switch:
            return { ...formField, renderField: () => <Switch /> };
        default:
            return formField;
    }
};

export interface CreateEditBuildBucketModalProps {
    bucket?: BuildDistroBucketBase;
    open: boolean;
    onCancel: () => void;
    appId: string;
    orgId: string;
}

// The fields, their options and their rules come from the backend, which checks the same ones on save
export const CreateEditBuildBucketModal: React.FC<CreateEditBuildBucketModalProps> = ({
    appId,
    orgId,
    open,
    onCancel,
    bucket,
}) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const isEditing = !!bucket;

    const { settingFields, isLoadingSettingFields } = useBuildDistroBucketSettingFields(
        orgId,
        appId,
        bucket?.id,
        !open,
    );
    // The rule's fields go in their own card, between the fields before and after them
    const { fieldsBeforeRule, ruleTypeField, ruleFields, fieldsAfterRule } = React.useMemo(() => {
        const fields = settingFields ?? [];
        const firstRuleIndex = fields.findIndex((field) => field.group === RULE_GROUP);
        const ruleStart = firstRuleIndex === -1 ? fields.length : firstRuleIndex;
        const [typeField, ...otherRuleFields] = fields.filter((field) => field.group === RULE_GROUP);
        return {
            fieldsBeforeRule: formFieldsFromSettingFields(
                fields.slice(0, ruleStart),
                undefined,
                resolveBucketFormField,
            ),
            ruleTypeField: typeField,
            ruleFields: formFieldsFromSettingFields(otherRuleFields, undefined, resolveBucketFormField),
            fieldsAfterRule: formFieldsFromSettingFields(
                fields.slice(ruleStart).filter((field) => field.group !== RULE_GROUP),
                undefined,
                resolveBucketFormField,
            ),
        };
    }, [settingFields]);
    // In the card, the select needs a rule picked and the heading above it carries the label
    const ruleTypeFormFields = React.useMemo(
        () =>
            ruleTypeField ?
                formFieldsFromSettingFields([
                    { ...ruleTypeField, isLabelHidden: true, required: true, hasDividerAbove: false },
                ]).map((field) => ({ ...field, style: { ...field.style, marginTop: 0 } }))
            :   [],
        [ruleTypeField],
    );
    const sourceScope = React.useMemo(() => ({ orgId, appId, bucketId: bucket?.id }), [orgId, appId, bucket?.id]);

    const isCreatingNewBuildDistributionBucket = useIsLoading(TRY_NEW_BUILD_DISTRIBUTION_BUCKET);
    const buildDistributionBucketError = useError(TRY_NEW_BUILD_DISTRIBUTION_BUCKET);
    const createBuildDistributionBucketSuccess = useUpdated(TRY_NEW_BUILD_DISTRIBUTION_BUCKET);
    const isUpdatingBuildDistributionBucket = useIsLoading(
        BuildDistributionUpdatable.UpdateBuildDistroBucket,
        bucket?.id,
    );
    const updateBuildDistributionBucketError = useError(BuildDistributionUpdatable.UpdateBuildDistroBucket, bucket?.id);
    const updateBuildDistributionBucketSuccess = useUpdated(
        BuildDistributionUpdatable.UpdateBuildDistroBucket,
        bucket?.id,
    );

    React.useEffect(() => {
        if (createBuildDistributionBucketSuccess) {
            globalMessage.success('Bucket created successfully!');
        }
        if (updateBuildDistributionBucketSuccess) {
            globalMessage.success('Bucket updated successfully!');
        }

        onCancel();
    }, [createBuildDistributionBucketSuccess, updateBuildDistributionBucketSuccess]);

    React.useEffect(() => {
        if (buildDistributionBucketError) {
            globalMessage.error(buildDistributionBucketError);
        }

        if (updateBuildDistributionBucketError) {
            globalMessage.error(updateBuildDistributionBucketError);
        }
    }, [buildDistributionBucketError, updateBuildDistributionBucketError]);

    // A bucket with a saved rule opens with its card; Add a rule opens an empty one
    const [hasRule, setHasRule] = React.useState(false);

    // Fields arrive after the modal opens, and their saved values are the form's initial values
    React.useEffect(() => {
        if (open && settingFields) {
            form.resetFields();
            setHasRule(!!ruleTypeField?.currentValue);
        }
    }, [open, bucket?.id, settingFields, form]);

    const name = Form.useWatch('name', form);
    // The bucket's identifier sits under its name
    const nameFieldEnd = fieldsBeforeRule.findIndex((field) => field.name === 'name') + 1;
    // A rule can't be added while every rule type is disabled, like without a supported CI integration
    const ruleTypeOptions: SettingOption[] = Form.useWatch(
        (values) =>
            ruleTypeField?.optionsBy ?
                (ruleTypeField.optionsBy.options[String(get(values, ruleTypeField.optionsBy.field))] ?? [])
            :   (ruleTypeField?.options ?? []),
        form,
    );
    const addRuleDisabledReason =
        ruleTypeOptions?.length && ruleTypeOptions.every((option) => option.disabledReason) ?
            ruleTypeOptions[0].disabledReason
        :   undefined;

    // Empties every rule field, so adding a rule again starts fresh; null rather than undefined, which a
    // remounted field would fill with the saved value
    const deleteRule = () => {
        const emptyRule = {};
        for (const field of settingFields ?? []) {
            if (field.group === RULE_GROUP) {
                set(emptyRule, field.key.slice(`${RULE_GROUP}.`.length), field.defaultValue ?? null);
            }
        }
        form.setFieldValue(RULE_GROUP, emptyRule);
        setHasRule(false);
    };

    // A form without its fields would save them cleared, so it waits for them; the values are the payload as-is
    const createEditBucket = (values) => {
        const requestData = { ...values, rule: { ...values.rule, type: values.rule?.type ?? '' }, appId, orgId };
        dispatch(
            isEditing ?
                tryUpdateBuildDistributionBucket(BuildDistributionUpdatable.UpdateBuildDistroBucket, {
                    bucketId: bucket.id,
                    ...requestData,
                })
            :   tryNewBuildDistributionBucket({
                    members: [],
                    ...requestData,
                } as CreateBuildDistributionBucketRequest),
        );
    };

    return (
        <Modal
            width={600}
            open={open}
            title={isEditing ? `Edit bucket` : 'Create bucket'}
            okText={isEditing ? 'Save' : `Create ${name ? `'${name}' bucket` : 'bucket'}`}
            onOk={() => {
                form.validateFields()
                    .then(() => {
                        form.submit();
                    })
                    .catch(() => null);
            }}
            okButtonProps={{
                loading: isCreatingNewBuildDistributionBucket || isUpdatingBuildDistributionBucket,
                disabled: !settingFields || isLoadingSettingFields,
            }}
            onCancel={onCancel}
            destroyOnClose
        >
            {!settingFields ?
                <Spin />
            :   <SettingOptionsSourceContext.Provider value={sourceScope}>
                    <Form form={form} layout="vertical" onFinish={createEditBucket}>
                        {!hasRule && (
                            <Alert
                                type="warning"
                                showIcon
                                message="If no bucket rules are set, artifacts will need to be uploaded via Public API"
                                style={{ marginBottom: 24 }}
                            />
                        )}
                        {renderFormFields(fieldsBeforeRule.slice(0, nameFieldEnd))}
                        {isEditing && (
                            <Space style={{ marginTop: 4, marginBottom: 24 }} align="center">
                                <Text type="secondary">Bucket identifier: </Text>
                                <Text
                                    style={{
                                        backgroundColor: antGrey.grey5,
                                        color: runwayColors.darkTextGrey,
                                        borderRadius: 3,
                                        borderStyle: 'solid',
                                        borderColor: antGrey.grey7,
                                        borderWidth: 1,
                                        padding: '2px 4px',
                                    }}
                                >
                                    {bucket.id}
                                </Text>
                            </Space>
                        )}
                        {renderFormFields(fieldsBeforeRule.slice(nameFieldEnd))}
                        {ruleTypeField && (
                            <>
                                {ruleTypeField.hasDividerAbove && <Divider />}
                                <Space size={8} style={{ display: 'flex', marginBottom: 12 }}>
                                    <Text>{ruleTypeField.label}</Text>
                                    {ruleTypeField.helpText && (
                                        <TooltipInfoIcon tooltipContent={ruleTypeField.helpText} />
                                    )}
                                </Space>
                                {addRuleDisabledReason && (
                                    <Text type="warning" style={{ display: 'block' }}>
                                        {addRuleDisabledReason}
                                    </Text>
                                )}
                                {hasRule ?
                                    <div
                                        style={{
                                            width: '100%',
                                            backgroundColor: antGrey.grey2,
                                            borderRadius: 8,
                                            padding: 20,
                                            marginTop: 12,
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                {renderFormFields(ruleTypeFormFields, 0)}
                                            </div>
                                            <Popconfirm
                                                title="Delete this rule?"
                                                description="This bucket will have no rules until you add another."
                                                onConfirm={deleteRule}
                                                okButtonProps={{ danger: true }}
                                                okText="Delete"
                                                placement="left"
                                            >
                                                <Button
                                                    icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                                    danger
                                                    type="link"
                                                />
                                            </Popconfirm>
                                        </div>
                                        {renderFormFields(ruleFields)}
                                    </div>
                                :   <Button
                                        icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                        style={{ marginTop: 12 }}
                                        disabled={!!addRuleDisabledReason}
                                        onClick={() => setHasRule(true)}
                                    >
                                        Add a rule
                                    </Button>
                                }
                            </>
                        )}
                        {renderFormFields(fieldsAfterRule)}
                        {!isEditing && (
                            <>
                                <Form.Item hidden name="members" />
                                <Divider />
                                <AddEditBucketMembers
                                    initialMembers={[]}
                                    excludedMembers={[]}
                                    appId={appId}
                                    orgId={orgId}
                                    onMembersChange={(members = []) => {
                                        form.setFieldsValue({ members });
                                    }}
                                />
                            </>
                        )}
                    </Form>
                </SettingOptionsSourceContext.Provider>
            }
        </Modal>
    );
};

// Form.Item passes value and onChange through to the Select
const BuildSystemSelect: React.FC<{ settingField: SettingField }> = ({ settingField, ...selectProps }) => {
    const { appId } = React.useContext(SettingOptionsSourceContext);
    const buildSystemIntegrations = useSelector((state: RootState) => selectBuildSystemIntegrations(state, appId));
    const destinationInfoByDestinationStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );
    const stepGraph = useSelector((state: RootState) => selectStepGraph(state, appId));
    const orgTags = useSelector((state: RootState) => selectOrg(state)?.tags ?? []);
    const destinationsByIntegrationId = React.useMemo(
        () => destinationsByIntegrationInstallationId(stepGraph, destinationInfoByDestinationStepConfigId, orgTags),
        [stepGraph, destinationInfoByDestinationStepConfigId, orgTags],
    );

    const options = (settingField.options ?? []).map((option) => {
        const integration = buildSystemIntegrations.find((candidate) => candidate.installationId === option.value);
        if (!integration) {
            return { value: option.value, label: option.label, searchText: option.label };
        }
        const alias = typeof integration.alias === 'string' ? integration.alias.trim() : '';
        return {
            value: option.value,
            searchText: [integration.name, alias, displayNameForIntegrationType(integration.id)]
                .filter(Boolean)
                .join(' '),
            label: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <Space size={4}>
                        <Text>{integration.name}</Text>
                        {alias && <Text type="secondary">{`(${alias})`}</Text>}
                    </Space>
                    <DestinationCounter
                        countOnly
                        size={20}
                        destinations={destinationsByIntegrationId[integration.installationId] ?? []}
                    />
                </div>
            ),
        };
    });

    return (
        <Select
            showSearch
            placeholder={settingField.placeholder}
            options={options}
            filterOption={(input, option) => `${option?.searchText ?? ''}`.toLowerCase().includes(input.toLowerCase())}
            {...selectProps}
        />
    );
};

// App users' VCS accounts first, with their names, then the repository's other contributors
const VCSUsersSelect: React.FC = (selectProps) => {
    const { orgId, appId, bucketId } = React.useContext(SettingOptionsSourceContext);
    const vcsIntegration = useSelector((state: RootState) => selectVCSIntegration(state, appId));
    const { data: repoContributors } = useGetBuildDistroBucketRepoContributorsQuery(
        { orgId, appId, bucketId },
        { refetchOnMountOrArgChange: false },
    );
    const users: RunwayOrVCSUser[] = uniqBy(
        [
            ...(repoContributors?.vcsUsers ?? []),
            ...(repoContributors?.contributors ?? []).map((vcsUser) => ({ vcsUser })),
        ],
        (runwayOrVCSUser) => runwayOrVCSUser.vcsUser.username,
    );

    return (
        <Select
            mode="tags"
            allowClear
            style={{ width: '100%' }}
            options={users.map((runwayOrVCSUser) => ({
                value: runwayOrVCSUser.vcsUser.username,
                label: <RunwayOrVCSUserLabel runwayOrVCSUser={runwayOrVCSUser} vcsIntegrationId={vcsIntegration?.id} />,
            }))}
            {...selectProps}
        />
    );
};

const RunwayOrVCSUserLabel: React.FC<{ runwayOrVCSUser: RunwayOrVCSUser; vcsIntegrationId?: IntegrationType }> = ({
    runwayOrVCSUser: { vcsUser, user },
    vcsIntegrationId,
}) => {
    const name = user && displayUserNameShort(user);
    return (
        <Space align="center">
            {vcsIntegrationId && <IntegrationIcon size={20} integrationId={vcsIntegrationId} />}
            <Text>{vcsUser.username}</Text>
            {name && <Text type="secondary">{`(${name})`}</Text>}
        </Space>
    );
};
